import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { LocationUpdate } from '../../../ecaps-core/dialogs/elevation-dialog/models/location-update.model';
import { HelpService } from '../../../ecaps-core/services/help.service';
import { IHelpItem } from '../../../ecaps-core/services/models/interfaces/i-help-item.interface';
import { Group as LayoutConfigGroup } from '../../models/layout-config/group.model';
import { LayoutConfiguration } from '../../models/layout-config/layout-configuration.model';
import { Question as LayoutConfigQuestion } from '../../models/layout-config/question.model';

interface IAccordionGroup extends LayoutConfigGroup {
  expanded: boolean;
}

@Component({
  selector: 'layout-config-accordion',
  templateUrl: './layout-config-accordion.component.html',
  styleUrls: ['./layout-config-accordion.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class LayoutConfigAccordionComponent implements OnInit, OnChanges {
  @Input('layoutConfig') public layoutConfig: LayoutConfiguration;

  @Input() public multi: boolean = true;

  @Output('requiredCompleted') public requiredCompleted =
    new EventEmitter<void>();
  @Output('locationUpdated') public locationUpdated =
    new EventEmitter<LocationUpdate>();
  @Output('selectionChanged') public selectionChanged =
    new EventEmitter<void>();

  private questionsValid: { [key: string]: boolean } = {};

  protected accordionGroups: IAccordionGroup[];
  protected accordionHelp: Record<string, IHelpItem> = {};

  protected chromeVersion: number;

  private accordionExpansionMap: Record<string, boolean> = {
    Preconditioner_layoutConfig_Preconditioner_group_AdvancedInputs: false,
    Preconditioner_layoutConfig_Preconditioner_group_AdvancedConfiguration:
      false,
  };

  constructor(private helpService: HelpService) {
    const raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);

    this.chromeVersion = raw ? parseInt(raw[2], 10) : -1;
  }

  ngOnInit() {}

  ngOnChanges() {
    this.getAccordionGroups();
  }

  getAccordionGroups() {
    if (
      this.layoutConfig &&
      this.layoutConfig.group &&
      this.layoutConfig.group.groups &&
      this.layoutConfig.group.groups.length > 0
    ) {
      this.accordionGroups = this.layoutConfig.group.groups
        .filter((group) => group.visible)
        .map((group) => {
          const key = `${this.layoutConfig.productType}_layoutConfig_${this.layoutConfig.modelGroup}_group_${group.name}`;
          const newGroup: IAccordionGroup = group as IAccordionGroup;
          newGroup.expanded = this.accordionExpansionMap[key] ?? true;

          return newGroup;
        });

      this.questionsValid = {};

      this.accordionGroups.forEach((group) => {
        group.questions.forEach((question) => {
          this.questionsValid[question.name] = true;
        });
      });
    } else {
      this.accordionGroups = null;
    }

    this.accordionHelp = {};

    this.accordionGroups?.forEach(async (group) => {
      const key = `${this.layoutConfig.productType}_layoutConfig_${this.layoutConfig.modelGroup}_group_${group.name}`;

      const helpItem = await this.helpService.getHelp(key);

      if (helpItem) {
        this.accordionHelp[group.name] = helpItem;
      } else {
        this.accordionHelp[group.name] = {
          key,
        };
      }
    });

    return this.accordionGroups;
  }

  protected hasHelp(group: LayoutConfigGroup): boolean {
    return (
      !!this.accordionHelp[group.name] &&
      Object.keys(this.accordionHelp[group.name]).length > 1
    );
  }

  protected helpIconClick(group: LayoutConfigGroup): void {
    const helpItem = this.accordionHelp[group.name];

    this.helpService.showHelp(helpItem);
  }

  getAccordionQuestions(group: LayoutConfigGroup): Array<LayoutConfigQuestion> {
    if (group && group.questions && group.questions.length) {
      return group.questions.filter((question) => question.visible);
    }
  }

  accordionClicked() {
    const self = this;

    setTimeout(() => {
      self.selectionChanged.next();
    }, 250);
  }

  setAutoFocus(question: LayoutConfigQuestion) {
    const firstQuestion = this.layoutConfig.group.groups
      .filter((subGroup) => subGroup.visible)[0]
      .questions.filter((groupQuestion) => groupQuestion.required)[0];

    return question === firstQuestion;
  }

  questionAnswered() {
    if (this.layoutConfig.requiredComplete()) {
      this.requiredCompleted.next();
    }
  }

  locationUpdatedEvent($event: LocationUpdate) {
    this.locationUpdated.next($event);
  }

  showWarning(group: LayoutConfigGroup): boolean {
    if (!!this.questionsValid) {
      let invalid = false;

      group.questions.forEach((question) => {
        if (!this.questionsValid[question.name]) {
          invalid = true;
        }
      });

      return invalid;
    } else {
      return false;
    }
  }

  validChanged(question: LayoutConfigQuestion, valid: boolean) {
    if (!!this.questionsValid) {
      this.questionsValid[question.name] = valid;
    }
  }
}
